import React from "react";
import { Box } from "@mui/material";
import {
  RocketLaunchOutlined,
  GroupsOutlined,
  SendOutlined,
  CreditCardOutlined,
} from "@mui/icons-material";
import { formatCount } from "utils/chartsPalette";
import { T, cardSx } from "utils/backrrTokens";
import StatTile from "./StatTile";

// Four separate cards with a gap, matching startup-web's Performance summary
// row rather than the single hairline-split surface this used before — the two
// apps now read as one product.
//
// This row replaces what were EIGHT cards: the all-time counts that had four
// cards of their own (two of them both labelled "Registered Today") now ride in
// each tile's footnote.
//
// Invitations left this row when the pipeline card arrived: that card states
// the same total AND where those invitations now stand, so a tile repeating the
// bare count was the weaker of two places to read it. Match rate took the slot
// — the marketplace question the count only gestures at.
const TILES = [
  { key: "new_startups", label: "New Startups", icon: RocketLaunchOutlined, seriesKey: "startups" },
  { key: "new_investors", label: "New Investors", icon: GroupsOutlined, seriesKey: "investors" },
  { key: "invitations_today", label: "Round Invitations Today", icon: SendOutlined, seriesKey: "invitations" },
  // Onboarding rate gave up this slot. It read 0% because nothing in the scoped
  // population carries is_onboarded, and a tile that always says zero teaches an
  // admin to distrust the three beside it. This one comes from a different
  // service, so it is read off the monetization payload rather than `data`.
  {
    key: "subscriptions",
    label: "Subscriptions Purchased",
    icon: CreditCardOutlined,
    seriesKey: null,
    source: "monetization",
  },
];

const HINTS = {
  new_startups:
    "Startup profiles created in this range that have a name. Profiles with no name are skipped everywhere on this page, so every startup figure describes the same population.",
  new_investors: "Investor accounts created in this range.",
  invitations_today:
    "Round invitations sent today in Asia/Kolkata, with every invitation ever sent beneath. This one tile deliberately ignores the date filter — \"how many went out today\" is not a question about the selected range. Match rate moved to the Pipeline card.",
  subscriptions:
    "Subscriptions bought inside the selected range, against the lifetime total beneath. Records, not startups — two plans bought by one startup are two subscriptions — and only active or completed ones count, never an abandoned checkout.",
};

const KpiStrip = ({ data, monetization, isLoading }) => {
  const daily = data?.daily ?? [];

  const metricFor = (tile) =>
    tile.source === "monetization" ? monetization?.[tile.key] : data?.kpis?.[tile.key];

  const footnoteFor = (key) => {
    if (key === "new_startups" && data?.totals?.startups != null) {
      return `${formatCount(data.totals.startups)} all-time`;
    }
    if (key === "new_investors" && data?.totals?.investors != null) {
      return `${formatCount(data.totals.investors)} all-time`;
    }
    if (key === "invitations_today" && data?.totals?.invitations != null) {
      return `${formatCount(data.totals.invitations)} all-time`;
    }
    if (key === "subscriptions" && monetization?.totals?.subscriptions != null) {
      return `${formatCount(monetization.totals.subscriptions)} all-time`;
    }
    return "";
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
        gap: 2,
      }}
    >
      {TILES.map((tile) =>
        isLoading ? (
          <Box key={tile.key} className="skeleton" sx={{ ...cardSx, height: "7.5rem" }} />
        ) : (
          <StatTile
            key={tile.key}
            label={tile.label}
            icon={tile.icon}
            hint={HINTS[tile.key]}
            metric={metricFor(tile)}
            series={tile.seriesKey ? daily.map((d) => d[tile.seriesKey] ?? 0) : []}
            footnote={footnoteFor(tile.key)}
            color={T.accent}
          />
        ),
      )}
    </Box>
  );
};

export default KpiStrip;
